import { t } from "../lib/i18n";
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import logo from '../assets/logo.png';
import LanguageSwitcher from './LanguageSwitcher';
import { InstantUnderwritingWidget } from './InstantUnderwritingWidget';
import LeaseInsightWidget from './blog/LeaseInsightWidget';
import './HomePage.css';

const creditPackages = [
  {
    name: "Starter Pack",
    credits: 125,
    price: "$50",
    ukPrice: "£40",
    euPrice: "€45",
    description: "Perfect for initial deal screening and trying out AI lease abstraction.",
    features: [
      "125 analysis credits",
      "~10 full AI lease abstractions",
      "~25 institutional underwriting models",
      "1-click investment summary export",
      "Standard document vault access"
    ],
    popular: false,
  },
  {
    name: "Pro Pack",
    credits: 250,
    price: "$100",
    ukPrice: "£80",
    euPrice: "€92",
    description: "Best for active acquisitions teams, brokers, and asset managers.",
    features: [
      "250 analysis credits",
      "Full portfolio tracking & comps",
      "Multi-jurisdiction yield modeling (US/UK/EU)",
      "Instant market valuation & trends",
      "Priority AI processing queue"
    ],
    popular: true,
  },
  {
    name: "Business Pack",
    credits: 1700,
    price: "$500",
    ukPrice: "£400",
    euPrice: "€460",
    description: "For boutique funds, family offices, and high-volume acquisition pipelines.",
    features: [
      "1,700 analysis credits (25% bonus)",
      "Bulk lease batch processing",
      "Custom investor underwriting templates",
      "Multi-user team sharing & exports",
      "Dedicated account onboarding"
    ],
    popular: false,
  },
  {
    name: "Enterprise Pack",
    credits: 5000,
    price: "$1,000",
    ukPrice: "£800",
    euPrice: "€920",
    description: "Institutional scale for REITs, debt funds, and large portfolios.",
    features: [
      "5,000 analysis credits (50% bonus)",
      "Unlimited portfolio health tracking",
      "Full REST API & webhook integration",
      "Bespoke debt covenant extraction",
      "SLA & priority compliance support"
    ],
    popular: false,
  }
];

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-blue-600 selection:text-white">
      <Helmet>
        <title>{t("homePage.k9")}</title>
        <meta
          name="description"
          content="High-precision AI underwriting, automated lease abstraction, and commercial portfolio modeling across the US, UK, and European markets. Calculate Cap Rates, NIY, and cash flows in seconds."
        />
        <link rel="canonical" href="https://credominion.com/" />
      </Helmet>

      {/* Top Header / Navigation */}
      <header className="sticky top-0 inset-x-0 z-40 flex items-center justify-between px-6 md:px-12 py-3 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-sm">
        <Link to="/" className="flex items-center gap-3 no-underline">
          <img
            src={logo}
            alt={t("homePage.k10")}
            className="h-20 sm:h-24 md:h-28 w-auto object-contain hover:opacity-95 transition-opacity"
          />
        </Link>
        <div className="flex items-center gap-3 md:gap-6 text-sm font-semibold">
          <a
            href="#deal-screener"
            className="hidden sm:inline-block text-slate-700 hover:text-blue-600 transition-colors no-underline"
          >
            {t("homePage.k11")} </a>
          <a
            href="#pricing"
            className="hidden sm:inline-block text-slate-700 hover:text-blue-600 transition-colors no-underline"
          >
            {t("homePage.k12")} </a>
          <Link
            to="/blog"
            className="text-slate-700 hover:text-blue-600 transition-colors no-underline"
          >
            {t("homePage.k13")} </Link>
          <Link
            to="/contact"
            className="text-slate-700 hover:text-blue-600 transition-colors no-underline hidden sm:inline"
          >
            {t("homePage.k14")} </Link>
          <LanguageSwitcher />
          <Link
            to="/login"
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all no-underline text-xs md:text-sm"
          >
            {t("homePage.k15")} </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-12 pb-16 md:pt-16 md:pb-24 px-4 md:px-8 overflow-hidden bg-gradient-to-b from-blue-50/70 via-white to-slate-50 border-b border-slate-200/80">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-slate-900 tracking-tight leading-tight md:leading-none mb-6">
            {t("homePage.k16")} <br className="hidden md:inline" />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700">
              {t("homePage.k17")} </span>
          </h1>

          <p className="max-w-3xl mx-auto text-base sm:text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
            {t("homePage.k18")} </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#deal-screener"
              className="w-full sm:w-auto px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-base rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 active:translate-y-0 text-center no-underline"
            >
              {t("homePage.k19")} </a>
            <Link
              to="/login"
              className="w-full sm:w-auto px-8 py-3.5 bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 font-semibold text-base rounded-xl shadow-sm hover:shadow transition-all text-center no-underline"
            >
              {t("homePage.k20")} </Link>
          </div>

          <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-xs text-slate-500 font-medium">
            <div className="flex items-center gap-2">
              <span className="text-emerald-600 font-bold">✓</span>
              <span>{t("homePage.k21")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-emerald-600 font-bold">✓</span>
              <span>{t("homePage.k22")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-emerald-600 font-bold">✓</span>
              <span>{t("homePage.k23")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: The Un-Gated Deal Screener */}
      <section id="deal-screener" className="px-4 md:px-8 py-10 bg-slate-50">
        <InstantUnderwritingWidget />
      </section>

      {/* Section 2: Core Platform Capabilities */}
      <section className="py-20 px-4 md:px-8 max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold tracking-wide uppercase mb-3">
            {t("homePage.k24")} </div>
          <h3 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
            {t("homePage.k25")} </h3>
          <p className="text-slate-600 text-base mt-3">
            {t("homePage.k26")} </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Card 1: Lease Abstraction */}
          <div className="p-7 bg-white border border-slate-200/90 rounded-2xl shadow-sm hover:shadow-xl hover:border-blue-400/80 transition-all duration-300 group">
            <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 mb-5 group-hover:scale-110 group-hover:bg-blue-100/70 transition-transform">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">{t("homePage.k27")}</h4>
            <p className="text-slate-600 text-sm leading-relaxed">
              {t("homePage.k28")} </p>
          </div>

          {/* Card 2: Multi-Jurisdiction Financial Modeling */}
          <div className="p-7 bg-white border border-slate-200/90 rounded-2xl shadow-sm hover:shadow-xl hover:border-indigo-400/80 transition-all duration-300 group">
            <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 mb-5 group-hover:scale-110 group-hover:bg-indigo-100/70 transition-transform">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">{t("homePage.k29")}</h4>
            <p className="text-slate-600 text-sm leading-relaxed">
              {t("homePage.k30")} </p>
          </div>

          {/* Card 3: AI-Driven Deal Sourcing */}
          <div className="p-7 bg-white border border-slate-200/90 rounded-2xl shadow-sm hover:shadow-xl hover:border-purple-400/80 transition-all duration-300 group">
            <div className="w-12 h-12 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600 mb-5 group-hover:scale-110 group-hover:bg-purple-100/70 transition-transform">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <circle cx="12" cy="12" r="5" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v3m0 14v3M2 12h3m14 0h3" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">{t("homePage.k31")}</h4>
            <p className="text-slate-600 text-sm leading-relaxed">
              {t("homePage.k32")} </p>
          </div>

          {/* Card 4: Predictive Market Trend Analysis */}
          <div className="p-7 bg-white border border-slate-200/90 rounded-2xl shadow-sm hover:shadow-xl hover:border-emerald-400/80 transition-all duration-300 group">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 mb-5 group-hover:scale-110 group-hover:bg-emerald-100/70 transition-transform">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">{t("homePage.k33")}</h4>
            <p className="text-slate-600 text-sm leading-relaxed">
              {t("homePage.k34")} </p>
          </div>

          {/* Card 5: Instant Valuation & DCF */}
          <div className="p-7 bg-white border border-slate-200/90 rounded-2xl shadow-sm hover:shadow-xl hover:border-blue-400/80 transition-all duration-300 group">
            <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 mb-5 group-hover:scale-110 group-hover:bg-blue-100/70 transition-transform">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">{t("homePage.k35")}</h4>
            <p className="text-slate-600 text-sm leading-relaxed">
              {t("homePage.k36")} </p>
          </div>

          {/* Card 6: Enterprise Vault & Security */}
          <div className="p-7 bg-white border border-slate-200/90 rounded-2xl shadow-sm hover:shadow-xl hover:border-slate-400/80 transition-all duration-300 group">
            <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 mb-5 group-hover:scale-110 group-hover:bg-slate-200/70 transition-transform">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">{t("homePage.k37")}</h4>
            <p className="text-slate-600 text-sm leading-relaxed">
              {t("homePage.k38")} </p>
          </div>
        </div>
      </section>

      {/* Section 3: Live Interactive AI Lease Analyzer Hook */}
      <section className="py-20 px-4 md:px-8 bg-slate-100/70 border-y border-slate-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 border border-indigo-200 rounded-full text-indigo-700 text-xs font-bold uppercase tracking-wider mb-3">
              <span>{t("homePage.k39")}</span>
            </div>
            <h3 className="text-3xl font-extrabold text-slate-900">
              {t("homePage.k40")} </h3>
            <p className="text-slate-600 text-sm mt-2 max-w-2xl mx-auto">
              {t("homePage.k41")} </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 shadow-xl">
            <LeaseInsightWidget slug="homepage" />
          </div>
        </div>
      </section>

      {/* Section 4: Transparent Sovereign Pricing Section */}
      <section id="pricing" className="py-24 px-4 md:px-8 max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full text-emerald-700 text-xs font-bold uppercase tracking-wider mb-3">
            <span>{t("homePage.k42")}</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight">
            {t("homePage.k43")} </h2>
          <p className="text-slate-600 text-base mt-3">
            {t("homePage.k44")} </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {creditPackages.map((pkg, idx) => (
            <div
              key={idx}
              className={`p-6 rounded-2xl flex flex-col justify-between transition-all ${
                pkg.popular
                  ? 'bg-white border-2 border-blue-600 shadow-2xl ring-4 ring-blue-50 transform lg:-translate-y-2'
                  : 'bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300'
              }`}
            >
              <div>
                {pkg.popular && (
                  <span className="inline-block px-3 py-1 bg-blue-600 text-white text-[11px] font-bold rounded-full uppercase tracking-wider mb-4 shadow-sm">
                    {t("homePage.k45")} </span>
                )}
                <h3 className="text-xl font-bold text-slate-900 mb-1">{pkg.name}</h3>
                <p className="text-slate-600 text-xs mb-4 min-h-[36px]">{pkg.description}</p>

                <div className="mb-4 pb-4 border-b border-slate-100">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold text-slate-900">{pkg.price}</span>
                    <span className="text-xs text-slate-500">{t("homePage.k46")}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {t("homePage.k47")} {pkg.ukPrice} • {pkg.euPrice}
                  </div>
                  <div className="mt-2 text-xs font-bold text-blue-600">
                    {pkg.credits.toLocaleString()} {t("homePage.k48")} </div>
                </div>

                <ul className="space-y-2.5 text-xs text-slate-600 mb-6">
                  {pkg.features.map((feat, fIdx) => (
                    <li key={fIdx} className="flex items-start gap-2">
                      <span className="text-emerald-600 font-bold shrink-0">✓</span>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to="/login"
                className={`w-full py-2.5 px-4 text-center rounded-xl font-bold text-xs transition-all no-underline ${
                  pkg.popular
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200'
                }`}
              >
                {t("homePage.k49")} </Link>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center text-xs text-slate-500 max-w-xl mx-auto">
          {t("homePage.k50")} <a href="mailto:support@credominion.com" className="text-blue-600 underline font-semibold">support@credominion.com</a>.
        </div>
      </section>

      {/* Section 5: Bottom Call to Action */}
      <section className="px-4 md:px-8 py-12">
        <div className="py-16 px-6 md:px-12 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white rounded-3xl max-w-5xl mx-auto shadow-2xl text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
            {t("homePage.k51")} </h2>
          <p className="text-slate-300 text-base md:text-lg mb-8 leading-relaxed max-w-2xl mx-auto">
            {t("homePage.k52")} </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
              to="/login"
              className="w-full sm:w-auto px-8 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-base rounded-xl shadow-xl transition-all transform hover:scale-105 active:scale-95 no-underline"
            >
              {t("homePage.k53")} </Link>
            <a
              href="#deal-screener"
              className="w-full sm:w-auto px-8 py-3.5 bg-slate-800/80 hover:bg-slate-800 border border-slate-700 text-slate-200 font-semibold text-base rounded-xl transition-all no-underline"
            >
              {t("homePage.k54")} </a>
          </div>
          <p className="text-xs text-slate-400 mt-5">
            {t("homePage.k55")} </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer relative overflow-hidden pt-12 pb-[140px] border-t border-slate-200 bg-white px-6 md:px-12 text-slate-600">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8">
          <div className="flex flex-col">
            <div className="flex items-center gap-3 mb-3">
              <img
                src={logo}
                alt={t("homePage.k10")}
                className="h-20 md:h-24 w-auto object-contain hover:opacity-95 transition-opacity"
              />
            </div>
            <p className="text-xs text-slate-500 max-w-sm mb-3">
              {t("homePage.k56")} </p>
            <div className="text-xs text-slate-400">
              <p>{t("homePage.k57")} {new Date().getFullYear()} {t("homePage.k58")}</p>
            </div>
          </div>

          <div className="flex flex-col md:items-end space-y-4">
            <div className="flex items-center space-x-5">
              <a
                href="https://www.facebook.com/CREDominion.Official/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-700 transition-colors"
                aria-label={t("homePage.k59")}
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a
                href="https://www.instagram.com/credominion/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-700 transition-colors"
                aria-label={t("homePage.k60")}
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a
                href="https://www.linkedin.com/company/cre-dominion"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-700 transition-colors"
                aria-label={t("homePage.k61")}
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.credominion.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-700 transition-colors"
                aria-label={t("homePage.k62")}
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M3.609 1.814L13.792 12 3.61 22.186a2.38 2.38 0 0 1-.61-.954V2.768c.15-.36.368-.69.609-.954zm11.24 11.24l2.428-2.428-11.83-6.83 9.402 9.258zm0 1.892L5.447 24.204l11.83-6.83-2.428-2.428zm3.484-2.11l2.58-1.49a1.5 1.5 0 0 0 0-2.672l-2.58-1.49-2.023 2.023 2.023 2.029z"/></svg>
              </a>
            </div>

            <div className="flex flex-wrap gap-4 text-xs text-slate-500 font-medium">
              <Link to="/about" className="hover:text-slate-900 no-underline transition-colors">About</Link>
              <Link to="/blog" className="hover:text-slate-900 no-underline transition-colors">{t("homePage.k13")}</Link>
              <Link to="/contact" className="hover:text-slate-900 no-underline transition-colors">{t("homePage.k14")}</Link>
              <Link to="/privacy-policy" className="hover:text-slate-900 no-underline transition-colors">{t("homePage.k63")}</Link>
              <Link to="/terms-of-service" className="hover:text-slate-900 no-underline transition-colors">{t("homePage.k64")}</Link>
              <Link to="/faq" className="hover:text-slate-900 no-underline transition-colors">{t("homePage.k65")}</Link>
            </div>
          </div>
        </div>

        <div
          className="-bottom-[50px] inset-x-0 flex justify-center font-bold text-slate-300 opacity-25 pointer-events-none select-none"
          style={{ fontSize: '12vw' }}
        >
          credominion.com
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
